import React, { useState } from 'react';
import { Lock, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ChangePasswordCard = () => {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) {
      toast({ title: 'Too short', description: 'New password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    if (form.next !== form.confirm) {
      toast({ title: 'Mismatch', description: 'New passwords do not match', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: form.current, new_password: form.next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.token) localStorage.setItem('token', res.data.token);
      toast({ title: 'Password changed successfully' });
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast({ title: 'Could not change password', description: typeof detail === 'string' ? detail : 'Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000]" data-testid="change-password-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-[#2563EB] border-2 border-black rounded-lg flex items-center justify-center">
          <Lock className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-black text-gray-900 dark:text-white">Change Password</h2>
          <p className="text-sm text-gray-500 dark:text-neutral-400">Update the password you use to sign in</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <div>
          <Label htmlFor="current-password" className="font-bold">Current Password</Label>
          <Input
            id="current-password"
            type="password"
            value={form.current}
            onChange={set('current')}
            required
            autoComplete="current-password"
            className="mt-1 border-2 border-black dark:border-neutral-600"
            data-testid="current-password-input"
          />
        </div>
        <div>
          <Label htmlFor="new-password" className="font-bold">New Password</Label>
          <Input
            id="new-password"
            type="password"
            value={form.next}
            onChange={set('next')}
            required
            autoComplete="new-password"
            placeholder="At least 8 characters"
            className="mt-1 border-2 border-black dark:border-neutral-600"
            data-testid="new-password-input"
          />
        </div>
        <div>
          <Label htmlFor="confirm-new-password" className="font-bold">Confirm New Password</Label>
          <Input
            id="confirm-new-password"
            type="password"
            value={form.confirm}
            onChange={set('confirm')}
            required
            autoComplete="new-password"
            className="mt-1 border-2 border-black dark:border-neutral-600"
            data-testid="confirm-new-password-input"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] font-bold border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
          data-testid="change-password-submit-btn"
        >
          {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</>) : 'Update Password'}
        </Button>
      </form>
    </div>
  );
};
